import React from 'react';
import styled from 'styled-components';
import { VersionItem } from './types';

const VersionIcon = styled.img`
  height: 1rem;
`;

const VersionLabel = styled.span`
  position: relative;
  display: block;
  font-size: 0.7em;
  font-weight: 700;
  height: 1rem;
  line-height: 1rem;
  text-transform: uppercase;
  overflow: hidden;
`;

const VersionLabelRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 0.5rem;
  margin-top: 0;
  white-space: nowrap;
`;

const VersionLabelMeasure = styled(VersionLabelRow)`
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
`;

const VersionText = styled.span`
  display: inline-block;
  margin-right: 0.25em;
`;

interface Props {
  items: VersionItem[];
  onShowTooltipClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

interface State {
  visibleCount: number;
  showOverflowIndicator: boolean;
}

const OverflowIndicator = styled.span`
  display: inline-block;
`;

class FitVersionLabel extends React.Component<Props, State> {
  private containerRef: HTMLSpanElement | null = null;
  private measureRef: HTMLSpanElement | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      visibleCount: props.items.length,
      showOverflowIndicator: false,
    };
    this.recalculateVisibleItems = this.recalculateVisibleItems.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.recalculateVisibleItems);
    this.recalculateVisibleItems();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.items !== this.props.items) {
      this.recalculateVisibleItems();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.recalculateVisibleItems);
  }

  recalculateVisibleItems() {
    const container = this.containerRef;
    const measure = this.measureRef;
    if (!container || !measure) {
      return;
    }

    const availableWidth = container.clientWidth;
    const nodes = Array.from(measure.children) as HTMLElement[];
    const itemNodes = nodes.slice(0, this.props.items.length);
    const indicatorNode = nodes[this.props.items.length];
    const indicatorWidth = indicatorNode ? Math.ceil(indicatorNode.getBoundingClientRect().width) : 0;

    let occupiedWidth = 0;
    let nextVisibleCount = 0;

    for (const node of itemNodes) {
      const nodeWidth = Math.ceil(node.getBoundingClientRect().width);
      if (occupiedWidth + nodeWidth > availableWidth) {
        break;
      }
      occupiedWidth += nodeWidth;
      nextVisibleCount += 1;
    }

    let showOverflowIndicator = nextVisibleCount < this.props.items.length;

    if (showOverflowIndicator) {
      occupiedWidth = 0;
      nextVisibleCount = 0;

      for (const node of itemNodes) {
        const nodeWidth = Math.ceil(node.getBoundingClientRect().width);
        if (occupiedWidth + nodeWidth + indicatorWidth > availableWidth) {
          break;
        }
        occupiedWidth += nodeWidth;
        nextVisibleCount += 1;
      }

      showOverflowIndicator = indicatorWidth <= availableWidth;
    }

    if (
      nextVisibleCount !== this.state.visibleCount
      || showOverflowIndicator !== this.state.showOverflowIndicator
    ) {
      this.setState({
        visibleCount: nextVisibleCount,
        showOverflowIndicator,
      });
    }
  }

  renderItem(item: VersionItem) {
    if (item.kind === 'icon' && typeof item.value === 'object') {
      return <React.Fragment key={item.key}>
        <VersionIcon src={item.value.traditional.default.png} alt={item.alt || ''} className='d-traditional-only' />
        <VersionIcon src={item.value.platinum.default.png} alt={item.alt || ''} className='d-platinum-only' />
        <VersionIcon src={item.value.market.default.png} alt={item.alt || ''} className='d-market-only' />
      </React.Fragment>;
    }

    if (item.kind === 'text' && typeof item.value === 'string') {
      return <VersionText key={item.key}>{item.value}</VersionText>;
    }

    return null;
  }

  render() {
    const visibleItems = this.props.items.slice(0, this.state.visibleCount);

    return (
      <VersionLabel innerRef={node => { this.containerRef = node; }}>
        <VersionLabelRow>
          {visibleItems.map(item => this.renderItem(item))}
          {this.state.showOverflowIndicator && <OverflowIndicator onClick={this.props.onShowTooltipClick}>
            <i className="fas fa-plus"></i>
          </OverflowIndicator>}
        </VersionLabelRow>
        <VersionLabelMeasure innerRef={node => { this.measureRef = node; }}>
          {this.props.items.map(item => this.renderItem(item))}
          <OverflowIndicator>
            <i className="fas fa-plus"></i>
          </OverflowIndicator>
        </VersionLabelMeasure>
      </VersionLabel>
    );
  }
}

export default FitVersionLabel;
